import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Mail, AlertTriangle } from "lucide-react";

const Stat = ({ label, value, tone }) => (
  <div className="border-2 border-foreground p-3">
    <p className={`font-display text-4xl leading-none ${tone || ""}`}>{value}</p>
    <p className="font-data text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
  </div>
);

export function EmailHealth() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/admin/email-stats");
      setStats(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load email stats.");
    } finally { setBusy(false); }
  };

  const onOpenChange = (v) => { setOpen(v); if (v) load(); };

  // quiet load on mount so the button can flag an active bounce alert
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
  const delivTone = stats?.deliverability >= 90 ? "text-[hsl(var(--pitch-green))]"
    : stats?.deliverability !== null && stats?.deliverability < 80 ? "text-destructive" : "";
  const bounceTone = stats?.bounce_rate >= 5 ? "text-destructive"
    : stats?.bounce_rate !== null ? "text-[hsl(var(--pitch-green))]" : "";
  const alertOn = !!stats?.alert_active;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button data-testid="admin-email-health-btn"
          title={alertOn ? "Bounce rate is high — check Mail Health" : "Email deliverability health (Resend)"}
          className={`hidden sm:inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] uppercase tracking-wider transition-colors ${alertOn ? "border-destructive text-destructive" : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}>
          <Mail size={12} /> Mail Health
          {alertOn && <span data-testid="admin-email-health-dot" className="ms-0.5 inline-block w-2 h-2 rounded-full bg-destructive animate-pulse" />}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-2 border-foreground rounded-none max-w-md" data-testid="admin-email-health-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase flex items-center gap-2">
            <Mail className="text-primary" size={24} /> Mail Health
          </DialogTitle>
          <DialogDescription className="font-data text-xs">
            Email activity over the last 15 days. Deliverability & bounce come from Resend delivery events.
          </DialogDescription>
        </DialogHeader>

        {busy && <p className="font-data text-xs text-muted-foreground" data-testid="admin-email-health-loading">Loading…</p>}
        {err && <p className="font-data text-xs text-destructive" data-testid="admin-email-health-error">{err}</p>}

        {stats && !busy && (
          <div className="space-y-3" data-testid="admin-email-health-body">
            {alertOn && (
              <div className="flex items-start gap-2 border-2 border-destructive text-destructive p-2" data-testid="admin-email-health-alert">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <p className="font-data text-[11px] leading-snug">
                  Bounce rate is above your {stats.alert_threshold}% threshold. Only email real, opted-in users
                  and consider pausing reminders until it recovers.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Deliverability" value={pct(stats.deliverability)} tone={delivTone} />
              <Stat label="Bounce Rate" value={pct(stats.bounce_rate)} tone={bounceTone} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Sent" value={stats.sent} />
              <Stat label="Delivered" value={stats.delivered} />
              <Stat label="Bounced" value={stats.bounced} tone={stats.bounced > 0 ? "text-destructive" : ""} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Skipped (test)" value={stats.skipped} />
              <Stat label="Complaints" value={stats.complained} tone={stats.complained > 0 ? "text-destructive" : ""} />
            </div>
            {!stats.webhook_configured && (
              <p className="font-data text-[11px] text-muted-foreground border border-border p-2" data-testid="admin-email-health-hint">
                To see live deliverability & bounce numbers, add a Resend webhook pointing to
                <span className="text-foreground"> /api/webhooks/resend</span> (events: delivered, bounced, complained).
                Until then, only Sent/Skipped are tracked.
              </p>
            )}
            {!stats.alert_email_set && (
              <p className="font-data text-[11px] text-muted-foreground border border-border p-2" data-testid="admin-email-health-alert-hint">
                Set an <span className="text-foreground">ALERT_EMAIL</span> secret to get an email if bounce
                rate ever exceeds {stats.alert_threshold}%. The in-app warning here works either way.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
